import {
  Box,
  Flex,
  Text,
  Badge,
  Icon,
  Tooltip,
  IconButton
} from '@chakra-ui/react'
import { FC, KeyboardEvent } from 'react'
import { FiChevronDown, FiChevronRight, FiClock } from 'react-icons/fi'

import {
  formatDate,
  formatFornecedor,
  getShortPedidoId
} from '@helper/mrp/approval'

import { IPendingOrderGroup } from '@base/@types/providers/Mrp/ApprovalProvider'

interface GroupHeaderProps {
  group: IPendingOrderGroup
  isExpanded: boolean
  onToggleExpand: () => void
}

const GroupHeader: FC<GroupHeaderProps> = ({
  group,
  isExpanded,
  onToggleExpand
}) => {
  const getStatusColor = (aprovado: boolean | null, hasAlteracoes: boolean) => {
    if (aprovado === true) return 'green'
    if (aprovado === false) return 'red'
    return hasAlteracoes ? 'orange' : 'blue'
  }

  const getStatusText = (aprovado: boolean | null, hasAlteracoes: boolean) => {
    if (aprovado === true) return 'Aprovado'
    if (aprovado === false) return 'Rejeitado'
    return hasAlteracoes ? 'Pendente - Com Alterações' : 'Pendente'
  }

  const getTipoProcessoLabel = (tipoProcesso: string) => {
    switch (tipoProcesso) {
      case '103':
        return 'COMPRA'
      case '108':
        return 'Compra programada'
      default:
        return tipoProcesso
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggleExpand()
    }
  }

  const toggleLabel = isExpanded ? 'Recolher pedido' : 'Expandir pedido'

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={handleKeyDown}
      borderWidth="1px"
      borderRadius="md"
      p={4}
      bg="white"
      _hover={{ bg: 'gray.50' }}
      cursor="pointer"
      onClick={onToggleExpand}
      border="1px solid"
      borderColor="gray.800"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        bg: 'green.500',
        borderRadius: '4px 0 0 4px'
      }}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={4}>
          <IconButton
            aria-label={toggleLabel}
            tabIndex={-1}
            icon={<Icon as={isExpanded ? FiChevronDown : FiChevronRight} />}
            size="sm"
            variant="ghost"
            onClick={e => {
              e.stopPropagation()
              onToggleExpand()
            }}
          />

          <Box>
            <Flex align="center" gap={2} mb={1}>
              <Text fontWeight="bold" fontSize="md" pr={2}>
                Pedido: {getShortPedidoId(group.idRequestSap)}
              </Text>

              {group.hasAlteracoes && (
                <Tooltip label="Este pedido possui alterações de quantidade">
                  <Icon as={FiClock} color="orange.500" />
                </Tooltip>
              )}
            </Flex>
            <Badge
              ml={2}
              colorScheme={getStatusColor(group.aprovado, group.hasAlteracoes)}
              variant="solid"
            >
              {getStatusText(group.aprovado, group.hasAlteracoes)}
            </Badge>
            <Badge colorScheme="blue" variant="subtle" fontSize="sm" ml={2}>
              {group.totalItems} {group.totalItems === 1 ? 'item' : 'itens'}
            </Badge>
            {/* Só informativo: estar fora do cronograma não exige aprovação */}
            {group.foraCronograma && (
              <Tooltip label="Pedido criado sem utilizar o Cronograma de Compras">
                <Badge
                  colorScheme="orange"
                  variant="subtle"
                  fontSize="sm"
                  ml={2}
                >
                  Fora do Cronograma
                </Badge>
              </Tooltip>
            )}

            <Box mt={2}>
              <Text fontSize="xs" color="gray.600">
                <Text as="span" fontWeight="semibold">
                  Solicitante:
                </Text>{' '}
                {group.usuarioSolicitanteLogin}
              </Text>
              <Text fontSize="xs" color="gray.600">
                <Text as="span" fontWeight="semibold">
                  Tipo:
                </Text>{' '}
                {getTipoProcessoLabel(group.tipoProcesso)}
              </Text>
              {group.fornecedores.length > 0 && (
                <Text fontSize="xs" color="gray.600" maxW="520px">
                  <Text as="span" fontWeight="semibold">
                    {group.fornecedores.length === 1
                      ? 'Fornecedor:'
                      : 'Fornecedores:'}
                  </Text>{' '}
                  {group.fornecedores.map(formatFornecedor).join(', ')}
                </Text>
              )}
              {group.foraCronograma && group.justificativaForaCronograma && (
                <Text fontSize="xs" color="gray.600" maxW="520px">
                  <Text as="span" fontWeight="semibold">
                    Justificativa (fora do cronograma):
                  </Text>{' '}
                  {group.justificativaForaCronograma}
                </Text>
              )}
            </Box>
          </Box>
        </Flex>

        <Box textAlign="right">
          <Flex direction="column" gap={1}>
            <Flex justify="flex-end" gap={4}>
              <Box
                textAlign="center"
                bg="blue.50"
                p={3}
                borderRadius="md"
                minW="100px"
                mr={2}
              >
                <Text fontSize="xs" color="gray.600" fontWeight="bold">
                  RECOMENDADO
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  {group.totalRecomendado.toLocaleString('pt-BR')}
                </Text>
              </Box>

              <Box
                textAlign="center"
                bg="green.50"
                p={3}
                borderRadius="md"
                minW="100px"
              >
                <Text fontSize="xs" color="gray.600" fontWeight="bold">
                  SOLICITADO
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="green.600">
                  {group.totalDesejado.toLocaleString('pt-BR')}
                </Text>
              </Box>

              {group.percentualAlteracaoGeral !== 0 && (
                <Box
                  textAlign="center"
                  bg={
                    group.percentualAlteracaoGeral > 0 ? 'green.50' : 'red.50'
                  }
                  p={3}
                  borderRadius="md"
                  minW="80px"
                >
                  <Text fontSize="xs" color="gray.600" fontWeight="bold">
                    ALTERAÇÃO
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      group.percentualAlteracaoGeral > 0
                        ? 'green.600'
                        : 'red.600'
                    }
                  >
                    {group.percentualAlteracaoGeral > 0 ? '+' : ''}
                    {group.percentualAlteracaoGeral.toFixed(1)}%
                  </Text>
                </Box>
              )}
            </Flex>
          </Flex>

          <Text fontSize="xs" color="gray.500" mt={2}>
            Solicitado em: {formatDate(group.dataSolicitacao)}
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

export default GroupHeader
